import { TaxonomicObjectAlertInput } from '@/models/Alerts'
import { BarcodeOption } from '@/models/Ean'
import { ProductExportTemplate } from '@/models/Export'
import { ChildProgress, Country, Culture, Language, LinkGroup, LocalEnvironment, Progress, ProgressType, ProgressTypes, SearchMode } from '@/models/Global'
import { ProductImportTemplate } from '@/models/Import'
import Message from '@/models/Message'
import { ProductType, SimplifiedProduct } from '@/models/Product'
import { PropertyTemplate, PropertyType, StoredProperty } from '@/models/Property'
import { CultureSettings, Settings } from '@/models/Settings'
import { TranslationDocument } from '@/models/Translation'
import { AuthRole, User } from '@/models/User'
import { triggerNavigation } from '@/router'
import { browserStorage } from '@/utils/browserStorage'
import { arrayToMap, getScrollbarWidth, toComparable } from '@/utils/tools'
import { defineStore } from 'pinia'
import { computed, ref, StyleValue, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTranslation } from '@/composables/useTranslation'

const DefaultLanguage: Language = 'sv'
export const DefaultCulture: Culture = {
  country: 'se',
  language: DefaultLanguage,
}

export const useDefaultStore = defineStore('default-store', () => {
  // ------------
  //  -- Setup --
  // ------------

  const router = useRouter()
  const route = useRoute()
  const { translate } = useTranslation()

  // --------------
  //  -- Statics --
  // --------------

  const localEnvironment = (import.meta.env.MODE as LocalEnvironment) || 'development'
  const themeEnvironment = localEnvironment
  const apiHost: string = import.meta.env.VITE_API_HOST || 'localhost:7821'

  // ---------------
  // -- Reactives --
  // ---------------

  const signingIn = ref(false)
  /** is undefined if uninitialized */
  const currentUser = ref<User | null>()
  const useNumericValuesForExport = ref(false)
  const showDiscreetAvailabilityStatuses = ref(browserStorage.get('discreet-availability-status') ?? false)
  const userRoles = ref<AuthRole[]>()

  const translationLanguage = ref<Language>(browserStorage.get('translationLanguage') || DefaultLanguage)
  const storedDataCulture = browserStorage.get('dataCulture')
  const dataCulture = ref<Culture | null>(storedDataCulture === undefined ? DefaultCulture : storedDataCulture)

  const selectedProductType = ref<ProductType | undefined>(browserStorage.get('selected-product-type') ?? 'product')

  /** Use to manipulate selected extra countries from the dropdown. Use includedCountries in API calls */
  const explicitlySelectedCountries = ref(new Set<Country>(browserStorage.get('selectedCountries')))

  /** Use sparcely to manipulate setting. Use effectivelyIncludeProductsMissingAvailableCountries to read */
  const explicityIncludeProductsMissingAvailableCountries = ref(!!browserStorage.get('includeProductsMissingAvailableCountries'))

  const translations = ref<TranslationDocument>()

  /** undefined when uninitialized, null when initializing */
  const templates = {
    export: ref<{ public: ProductExportTemplate[] | null | undefined; private: ProductExportTemplate[] | null | undefined }>({
      public: undefined,
      private: undefined,
    }),
    import: ref<{ public: ProductImportTemplate[] | null | undefined; private: ProductImportTemplate[] | null | undefined }>({
      public: undefined,
      private: undefined,
    }),
    properties: ref<{ public: PropertyTemplate[] | null | undefined; private: PropertyTemplate[] | null | undefined }>({
      public: undefined,
      private: undefined,
    }),
  }
  const settings = ref<Settings>()
  const links = ref<Partial<Record<'productsArchiveMenu', LinkGroup[]>>>({})
  const taxonomicObjectAlerts = ref<TaxonomicObjectAlertInput[]>()
  const availableBarcodeOptions = ref<BarcodeOption[]>()

  const confirmNavigationFrom = ref<string>()
  const evaluateNavigation = ref<{
    path: string
    cb?: () => Promise<boolean> | boolean
  }>()
  const temporaryIgnoreNavigationBlock = ref(false)

  const activeProgresses = ref(
    ProgressTypes.reduce(
      (acc, type) => {
        acc[type] = []
        return acc
      },
      {} as Record<ProgressType, Progress[]>,
    ),
  )
  const messages = ref<Message[]>([])
  const loadedAppDate = ref<Date>()
  const globalSearchMode = ref<SearchMode>('normal')
  const fullScreenSticky = ref(false)
  const modalOpen = ref(false)
  const alertsQueuedForRemoval = ref<{ [id: string]: TaxonomicObjectAlertInput }>({})
  const topMenuHeight = ref<number>(0)

  const lastActiveProduct = ref<SimplifiedProduct>()

  // -------------------------
  // -- Computed properties --
  // -------------------------

  /** Takes override-query into account */
  const overrideIncludeAllCultures = computed(() => route.query.includeAllCultures == '1')

  const includeAllProductTypes = computed(() => route.query.includeAllProductTypes == '1')

  const productType = computed({
    get: () => (includeAllProductTypes.value ? undefined : selectedProductType.value),
    set: (type: ProductType) => {
      selectedProductType.value = type

      if (includeAllProductTypes.value) {
        const query = { ...route.query }
        delete query.includeAllProductTypes
        router.replace({ query })
      }
    },
  })

  const availableCountries = computed(() => settings.value?.app.cultures.map((culture) => culture.country).sort((a, b) => a.localeCompare(b)) || [])

  const availableLanguages = computed(() => {
    const languages = settings.value?.app.cultures
      .map((culture) => culture.language)
      .sort((a, b) => translate(a, 'languages').localeCompare(translate(b, 'languages')))
    return Array.from(new Set(languages))
  })

  const availableCultures = computed(
    () =>
      settings.value?.app.cultures
        .map((data) => ({
          country: data.country,
          language: data.language,
        }))
        .sort((a, b) => translate(a.country, 'countries').localeCompare(translate(b.country, 'countries'))) || [],
  )

  const availableCulturesByCountry = computed(() => arrayToMap<CultureSettings, Country>(settings.value?.app.cultures || [], 'country'))

  const availableCulturesByLanguage = computed(() => arrayToMap<CultureSettings>(settings.value?.app.cultures || [], 'language'))

  const availableCulturesPlus = computed(
    (): Culture[] =>
      settings.value?.app.cultures
        .map((data) => ({
          country: data.country,
          language: data.language,
          mainLanguageCulture: data.mainLanguageCulture,
        }))
        .sort((a, b) => a.language.localeCompare(b.language)) || [],
  )

  const mainLanguageCountries = computed(() =>
    availableCulturesPlus.value.reduce((acc, culture) => {
      culture.mainLanguageCulture && acc.set(culture.language, culture.country)
      return acc
    }, new Map<Language, Country>()),
  )

  const allCulturesAreIncluded = computed(() => includedCountries.value.size === availableCountries.value.length)

  const productAvailabilityCodeToString = computed(() =>
    settings.value?.properties?.productAvailabilityOptions.reduce<Partial<Record<number, string>>>((acc, option) => {
      acc[option.code] = translate(option.label, 'terms', 'capitalize')
      return acc
    }, {}),
  )

  const availableTaxonomicObjectAlerts = computed(() => (taxonomicObjectAlerts.value?.length || 0) - Object.keys(alertsQueuedForRemoval.value).length)

  const topMenuStyle = computed((): StyleValue => ({ top: `${topMenuHeight.value}px` }))

  /** is undefined if uninitialized */
  const isSignedIn = computed(() => (currentUser.value === undefined ? undefined : !!currentUser.value))

  const userPrivileges = computed(() => new Set(currentUser.value?.privileges))

  const includedCountries = computed(() => {
    const countries = new Set(explicitlySelectedCountries.value)

    dataCulture.value && countries.add(dataCulture.value.country)

    if (overrideIncludeAllCultures.value) {
      availableCountries.value.forEach((country) => {
        countries.add(country)
      })
    }

    return countries
  })

  const implicitlyIncludedLanguages = computed(() =>
    Array.from(includedCountries.value).reduce((acc, country) => {
      const foundLanguage = availableCulturesByCountry.value.get(country as Country)?.language
      if (foundLanguage) {
        acc.add(foundLanguage)
      }
      return acc
    }, new Set<Language>()),
  )

  const includeProductsMissingAvailableCountries = computed(
    () => explicityIncludeProductsMissingAvailableCountries.value || !dataCulture.value || overrideIncludeAllCultures.value,
  )

  // --------------
  // -- Watchers --
  // --------------

  watch(
    () => settings.value?.properties?.productAvailabilityOptions,
    (options) => {
      if (!options) {
        return
      }

      const id = 'availability-colours'
      document.head.querySelector(`#${id}`)?.remove()

      const styleElement = document.createElement('style')
      styleElement.id = id
      styleElement.textContent = options
        .map((option) => {
          const mainSelector = `.availability-${option.label.replaceAll(' ', '-')}`
          const rule1 = `${mainSelector} { color: ${option.colour}; }`
          const rule2 = `${mainSelector}.discreet-availability-status { background-color: ${option.colour}; }`
          return [rule1, rule2].join('\n')
        })
        .join('\n')

      document.head.appendChild(styleElement)
    },
    {
      immediate: true,
    },
  )

  watch(
    () => explicitlySelectedCountries.value,
    (value) => {
      browserStorage.set('selectedCountries', Array.from(value))
    },
    {
      deep: true,
    },
  )

  watch(
    () => explicityIncludeProductsMissingAvailableCountries.value,
    (value) => {
      browserStorage.set('includeProductsMissingAvailableCountries', value)
    },
  )

  watch(
    () => selectedProductType.value,
    (value) => {
      if (value) {
        browserStorage.set('selected-product-type', value)
      } else {
        browserStorage.delete('selected-product-type')
      }
    },
  )

  // ---------------
  // -- Functions --
  // ---------------

  // #region Culture

  const toggleDiscreetAvailabilityStatuses = async () => {
    showDiscreetAvailabilityStatuses.value = !showDiscreetAvailabilityStatuses.value
    browserStorage.set('discreet-availability-status', showDiscreetAvailabilityStatuses.value)
  }

  const setMainDataCulture = async (culture: Culture | null) => {
    if (await triggerNavigation()) {
      dataCulture.value = culture
      browserStorage.set('dataCulture', culture)
    }
  }

  const toggleCountry = async (country: Country) => {
    if (await triggerNavigation()) {
      if (explicitlySelectedCountries.value.has(country)) {
        explicitlySelectedCountries.value.delete(country)
      } else {
        explicitlySelectedCountries.value.add(country)
      }
    }
  }

  const toggleAllCultures = async () => {
    if (await triggerNavigation()) {
      if (allCulturesAreIncluded.value) {
        explicitlySelectedCountries.value.clear()
        explicityIncludeProductsMissingAvailableCountries.value = false

        if (route.query.includeAllCultures) {
          const query = { ...route.query }
          delete query.includeAllCultures
          router.replace({ query })
        }
      } else {
        explicitlySelectedCountries.value = new Set(availableCountries.value)
        explicityIncludeProductsMissingAvailableCountries.value = true
      }
    }
  }

  const toggleIncludeProductsMissingAvailableCountries = async (explicitSetting?: boolean) => {
    if (await triggerNavigation()) {
      explicityIncludeProductsMissingAvailableCountries.value = explicitSetting ?? !explicityIncludeProductsMissingAvailableCountries.value
    }
  }

  // #region Progress

  const getProgressIndexById = (progress: Progress) => activeProgresses.value[progress.progressType].findIndex((progressBar) => progressBar.id === progress.id)

  /** Sets first progress */
  const setProgress = (progress: Progress) => {
    activeProgresses.value[progress.progressType][0] = progress
  }

  /** Update existing progress with the same id or creates a new one  */
  const setProgressById = (progress: Progress) => {
    const i = getProgressIndexById(progress)
    const existingProgress = activeProgresses.value[progress.progressType][i]

    if (existingProgress) {
      progress.progressLog = existingProgress.progressLog

      if (existingProgress.currentAction && progress.currentAction !== existingProgress.currentAction && existingProgress.percentage !== undefined) {
        progress.progressLog ??= new Set()
        progress.progressLog.add(existingProgress.currentAction)
      }

      activeProgresses.value[progress.progressType][i] = progress
    } else {
      activeProgresses.value[progress.progressType].push(progress)
    }
  }

  const addProgressBar = (progress: Progress) => {
    activeProgresses.value[progress.progressType].unshift(progress)
  }

  const clearProgress = (progressType: ProgressType) => {
    if (activeProgresses.value[progressType]?.length) {
      activeProgresses.value[progressType].length = 0
    }
  }

  const abortProgress = (progressType: ProgressType) => {
    const inProgress = activeProgresses.value[progressType]?.filter((progress) => progress.percentage != 100 && !progress.finished) as Progress[]
    for (const progressBar of inProgress) {
      progressBar.successful = false
      progressBar.finished = true

      progressBar.children?.forEach((child) => {
        child.successful = false
        child.finished = true
      })
    }
  }

  // #region Message

  const addMessage = (message: Message) => {
    messages.value.unshift(message)
    if (message.secondsVisible >= 0) {
      setTimeout(() => {
        messages.value = messages.value.filter((msg) => !msg.equals(message))
      }, message.secondsVisible * 1000)
    }
  }

  const removeMessage = (index: number) => {
    messages.value.splice(index, 1)
  }

  const setTranslationLanguage = (language: Language) => {
    translationLanguage.value = language
    browserStorage.set('translationLanguage', language)
  }

  const setModalOpen = (state: boolean) => {
    modalOpen.value = state
    if (state) {
      document.body.style.paddingRight = `${getScrollbarWidth()}px`
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.removeProperty('padding-right')
      document.body.style.removeProperty('overflow')
    }
  }

  return {
    // Auth/user related
    signingIn,
    currentUser,
    userRoles,
    useNumericValuesForExport,
    isSignedIn,
    userPrivileges,
    showDiscreetAvailabilityStatuses,
    toggleDiscreetAvailabilityStatuses,

    // stored data
    translations,
    templates,
    settings,
    taxonomicObjectAlerts,
    availableBarcodeOptions,
    links,

    // culture settings. affects api calls and app
    translationLanguage,
    infoLanguage: 'global',
    dataCulture,
    explicitlySelectedCountries,
    includedCountries,
    implicitlyIncludedLanguages,
    allCulturesAreIncluded,
    overrideIncludeAllCultures,
    availableCountries,
    availableLanguages,
    availableCultures,
    availableCulturesByCountry,
    availableCulturesByLanguage,
    availableCulturesPlus,
    mainLanguageCountries,
    explicityIncludeProductsMissingAvailableCountries,
    includeProductsMissingAvailableCountries,

    productAvailabilityCodeToString,

    // misc app state
    confirmNavigationFrom,
    evaluateNavigation,
    temporaryIgnoreNavigationBlock,
    activeProgresses,
    messages,
    loadedAppDate,
    globalSearchMode,
    fullScreenSticky,
    modalOpen,
    alertsQueuedForRemoval,
    topMenuHeight,
    cultureChangeFetchDelay: 1000,
    topMenuStyle,
    availableTaxonomicObjectAlerts,
    lastActiveProduct,
    productType,
    includeAllProductTypes,

    // env
    localEnvironment,
    themeEnvironment,
    apiHost,
    isDevEnvironment: localEnvironment === 'development',

    setProgress,
    setProgressById,
    addProgressBar,
    clearProgress,
    abortProgress,
    addMessage,
    removeMessage,
    setTranslationLanguage,
    setMainDataCulture,
    setModalOpen,
    toggleAllCultures,
    toggleCountry,
    toggleIncludeProductsMissingAvailableCountries,
  }
})
